import "@core/start-ui-injection";

import {
  getFactory,
  createPageRenderContext,
  RenderPageStructure,
} from "@newxsystems/fe-engine/core";
import type { ComponentFactory } from "@newxsystems/types";
import type { CmsPageLoadResult } from "./cms-page.server";
export { renderStartHead as renderCmsHead } from "@newxsystems/fe-engine/start";
import { hydrate, type DehydratedState, type QueryClient } from "@tanstack/react-query";
import type {
  IErrorBlockProps,
  IPageStructure,
  PageContext,
  PageSearchParams,
} from "@newxsystems/types";
import { BlocksFactory } from "@selected/template";
import { createNoStoreCacheHeaders } from "@core/cache/start-cache";
import { ErrorBlock } from "@core/components/ErrorBlock";
import { loadCmsPageServer } from "./cms-page.fn";
import { getStaticCmsDataPath } from "./static-cms-data";

declare const __NUX_START_STATIC_CMS_DATA__: boolean;

export type CmsRouteLoaderContext = {
  queryClient: QueryClient;
};

export type CmsRouteLoaderInput = {
  context: CmsRouteLoaderContext;
  path: string;
  searchParams?: PageSearchParams;
};

let renderFactory: ComponentFactory | undefined;

const ERROR_BLOCK_TYPE = "ErrorBlock";

function getRenderFactory(): ComponentFactory {
  if (!renderFactory) {
    renderFactory = getFactory();
    renderFactory.registerAll(BlocksFactory);
    if (!renderFactory.has("ErrorBlock")) {
      renderFactory.register("ErrorBlock", ErrorBlock);
    }
  }

  return renderFactory;
}

const normalizeSearchParamValue = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }

  const serialized = JSON.stringify(value);
  return typeof serialized === "string" ? serialized : undefined;
};

export const normalizeRouteSearchParams = (value: unknown): PageSearchParams => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const searchParams: PageSearchParams = {};

  for (const [key, rawValue] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(rawValue)) {
      const values = rawValue
        .map(normalizeSearchParamValue)
        .filter((item): item is string => item !== undefined);

      if (values.length > 0) {
        searchParams[key] = values.length > 1 ? values : values[0];
      }

      continue;
    }

    const normalizedValue = normalizeSearchParamValue(rawValue);

    if (normalizedValue !== undefined) {
      searchParams[key] = normalizedValue;
    }
  }

  return searchParams;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const isCmsPageLoadResult = (value: unknown): value is CmsPageLoadResult => {
  if (!isRecord(value)) return false;
  const page = value.page;

  return (
    isRecord(page) &&
    Array.isArray(page.items) &&
    isRecord(value.pageContext) &&
    isRecord(value.cache) &&
    isRecord(value.seo) &&
    "dehydratedState" in value
  );
};

const createPageContext = (path: string, searchParams: PageSearchParams): PageContext => ({
  pathname: path,
  pathParams: {},
  searchParams,
  slug: path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean),
  staticPage: true,
});

const createFallbackPage = (props: IErrorBlockProps): IPageStructure => ({
  items: [
    {
      components: [{ name: ERROR_BLOCK_TYPE, props }],
    },
  ],
});

const createClientErrorResult = ({
  description,
  path,
  searchParams,
  statusCode = 500,
  title = "Page unavailable",
}: {
  description: string;
  path: string;
  searchParams: PageSearchParams;
  statusCode?: number;
  title?: string;
}): CmsPageLoadResult => ({
  cache: createNoStoreCacheHeaders(path),
  dehydratedState: { mutations: [], queries: [] },
  page: createFallbackPage({
    description,
    homeHref: "/",
    homeLabel: "Go home",
    retryHref: path,
    retryLabel: "Try again",
    statusCode,
    title,
  }),
  pageContext: createPageContext(path, searchParams),
  seo: {
    description,
    locale: "en_US",
    noFollow: true,
    noIndex: true,
    openGraphType: "website",
    title,
  },
  statusCode,
});

const shouldUseStaticCmsData = () =>
  typeof window !== "undefined" && __NUX_START_STATIC_CMS_DATA__ === true;

const loadStaticCmsRoute = async (
  path: string,
  searchParams: PageSearchParams,
): Promise<CmsPageLoadResult> => {
  const response = await fetch(getStaticCmsDataPath(path), {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    return createClientErrorResult({
      description: "This page was not included in the deployed static page data.",
      path,
      searchParams,
      statusCode: response.status || 404,
      title: response.status === 404 ? "Page not found" : "Page unavailable",
    });
  }

  const result: unknown = await response.json().catch(() => undefined);

  if (!isCmsPageLoadResult(result)) {
    return createClientErrorResult({
      description: "The deployed static page data could not be read.",
      path,
      searchParams,
    });
  }

  return {
    ...result,
    pageContext: {
      ...result.pageContext,
      searchParams,
      staticPage: true,
    },
  };
};

const hydrateCmsData = (queryClient: QueryClient, data: CmsPageLoadResult) => {
  hydrate(queryClient, data.dehydratedState as unknown as DehydratedState);
};

export const loadCmsRoute = async ({
  context,
  path,
  searchParams = {},
}: CmsRouteLoaderInput): Promise<CmsPageLoadResult> => {
  if (shouldUseStaticCmsData()) {
    const result = await loadStaticCmsRoute(path, searchParams);
    hydrateCmsData(context.queryClient, result);
    return result;
  }

  const result = await loadCmsPageServer({ data: { path, searchParams } });
  hydrateCmsData(context.queryClient, result);
  return result;
};

export function CmsRoutePage({ data }: { data: CmsPageLoadResult }) {
  const context = createPageRenderContext({
    factory: getRenderFactory(),
    pageContext: data.pageContext,
  });

  // A route change renders the same block types in the same positions, so React
  // keeps the existing elements and their state — which would leave one page's
  // reveal, accordion, or form state on the next page. The pathname is the
  // identity of the page: a new one is a new tree.
  return <RenderPageStructure context={context} key={data.pageContext.pathname} page={data.page} />;
}
